import React, { useEffect, useState } from "react";
import {
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Radio,
  RadioGroup,
  Select,
  styled,
  TextField,
} from "@mui/material";
import AddEditPatientCheckboxField from "./AddEditPatientCheckboxField";
import AddEditPatientSelectField from "./AddEditPatientSelectField";
import AddEditPatientTextArea from "./AddEditPatientTextArea";
import DatePickerMui from "./DatePickerMui";
import { MuiTelInput } from "mui-tel-input";
import "./addeditpatient.css";
import { executeIntent } from "../../utils/IntentExecutor";
import IntentType from "../../../intentexecutor/utils/IntentType";
import { Spin } from "antd";
import { strings } from "../../utils/strings";
import PatientStatusSelect from "./PatientStatusSelect";
import DiagnosisSearchAndSelect from "./DiagnosisSearchAndSelect";
import GoogleAddressSelect from "./GoogleAddressSelect";
import TeamMultiselect from "./TeamMultiselect";
import FacilitySelect from "./FacilitySelect";
import { useGetOrgIdentifier } from "../../generic/hooks/useGetOrgIdentifier";

const AddEditPatientFormComponent = ({
  formErrors,
  patient,
  setPatient,
  metadata,
  setMetadata,
  patientDetailsLoading,
  setDiagnosis,
  diagnosis,
  setCareGiver,
  careGiver,
  label,
}) => {
  const organizationIdentifier = useGetOrgIdentifier();
  const [options, setOptions] = useState({});
  const [optionsLoading, setOptionsLoading] = useState(false);
  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPatient((prevUser) => ({
      ...prevUser,
      [name]: name === "facilityId" ? value?.facilityId : value,
      ...(name === "facilityId" && {
        facilityCompleteAddress: value?.facilityCompleteAddress,
      }),
    }));
  };

  const handleMetadataChange = (e) => {
    const { name, value } = e.target;
    setMetadata((prev) => {
      return {
        ...prev,
        [name]: value,
      };
    });
  };

  const handleCareGiverChange = (e) => {
    const { name, value } = e.target;
    setCareGiver((prev) => {
      return {
        ...prev,
        [name]: value,
      };
    });
  };

  const inputFieldSyle = {
    "& .MuiOutlinedInput-root": {
      color: "#000",
      "&.Mui-focused": {
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: "var(--main-theme-color) !important",
          borderWidth: "1px !important",
        },
      },
    },
    "& .MuiInputLabel-outlined": {
      "&.Mui-focused": {
        color: "var(--main-theme-color) !important",
        borderWidth: "1px !important",
      },
    },
  };

  const CustomOutlinedInput = styled(OutlinedInput)(({ theme }) => ({
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "var(--main-theme-color) !important",
      borderWidth: "1px !important",
    },
  }));

  useEffect(() => {
    const getOptions = async () => {
      setOptionsLoading(true);
      const payload = {
        configurationKey: ["niramaya.add.patient.options"],
      };

      const result = await executeIntent(
        IntentType.GET_CONFIGURATION,
        payload,
        {
          organizationIdentifier,
        },
      );
      const options =
        result[0].configurationValue &&
        JSON.parse(result[0].configurationValue);
      setOptions(options);
      setOptionsLoading(false);
    };

    getOptions();
  }, [organizationIdentifier]);

  return (
    <div>
      {optionsLoading || patientDetailsLoading ? (
        <Spin size="medium" className="d-flex justify-content-center m-auto" />
      ) : (
        <>
          <Box sx={{ flexGrow: 1 }}>
            <Grid container spacing={2}>
              <Grid xs={12} item>
                <h6 className="add-edit-patient-sub-header">
                  Patients Primary Details
                </h6>
              </Grid>

              <Grid xs={3} item>
                <TextField
                  fullWidth
                  value={patient?.pronoun}
                  name="pronoun"
                  label="Pronoun"
                  variant="outlined"
                  onChange={handleChange}
                  sx={inputFieldSyle}
                  InputLabelProps={{
                    shrink: patient?.pronoun,
                  }}
                />
              </Grid>
              <Grid xs={4.5} item>
                <TextField
                  error={formErrors?.givenName}
                  required
                  fullWidth
                  value={patient?.givenName}
                  name="givenName"
                  label="First Name"
                  variant="outlined"
                  helperText={formErrors?.givenName}
                  onChange={handleChange}
                  sx={inputFieldSyle}
                  InputLabelProps={{
                    shrink: patient?.givenName,
                  }}
                />
              </Grid>
              <Grid xs={4.5} item>
                <TextField
                  error={formErrors?.familyName}
                  required
                  fullWidth
                  value={patient?.familyName}
                  name="familyName"
                  label="Last Name"
                  helperText={formErrors?.familyName}
                  onChange={handleChange}
                  sx={inputFieldSyle}
                  InputLabelProps={{
                    shrink: patient?.familyName,
                  }}
                />
              </Grid>
              <Grid xs={6} item>
                <TextField
                  fullWidth
                  value={metadata?.preferredName}
                  name="preferredName"
                  label="Preferred Name"
                  onChange={handleMetadataChange}
                  sx={inputFieldSyle}
                  InputLabelProps={{
                    shrink: patient?.preferredName,
                  }}
                />
              </Grid>
              <Grid xs={6} item>
                <TextField
                  error={formErrors?.medicalRecordNumber}
                  required
                  fullWidth
                  value={patient?.medicalRecordNumber}
                  name="medicalRecordNumber"
                  label="Medical Record No."
                  helperText={formErrors?.medicalRecordNumber}
                  onChange={handleChange}
                  sx={inputFieldSyle}
                  InputLabelProps={{
                    shrink: patient?.medicalRecordNumber,
                  }}
                />
              </Grid>
              <Grid xs={12} item>
                <MuiTelInput
                  error={formErrors?.telephone}
                  fullWidth
                  defaultCountry="US"
                  value={patient?.telephone}
                  forceCallingCode
                  onChange={(value) =>
                    setPatient({ ...patient, telephone: value })
                  }
                  helperText={formErrors?.telephone}
                  sx={inputFieldSyle}
                />
              </Grid>
              <Grid xs={12} sm={6} item>
                <DatePickerMui
                  setData={setPatient}
                  data={patient}
                  name="admitDate"
                  label="Admit Date"
                  inputFieldSyle={inputFieldSyle}
                />
              </Grid>
              <Grid xs={12} sm={6} item>
                <PatientStatusSelect
                  data={options?.patientStatus}
                  metadata={patient}
                  handleMetadataChange={handleChange}
                />
              </Grid>
              <Grid xs={12} item>
                <TeamMultiselect
                  data={patient}
                  setData={setPatient}
                  isMultiSelect={false}
                  error={formErrors}
                  label={label}
                />
              </Grid>
              <Grid xs={12} item>
                <FormControl
                  required
                  error={Boolean(formErrors?.serviceLocationType)}
                  className="service-location-type"
                >
                  <FormLabel id="serviceLocationType">
                    Service Location
                  </FormLabel>
                  <RadioGroup
                    row
                    aria-labelledby="serviceLocationType"
                    name="serviceLocationType"
                    value={patient?.serviceLocationType || ""}
                    onChange={handleChange}
                  >
                    <FormControlLabel
                      value="facility"
                      control={<Radio />}
                      label="Facility"
                    />
                    <FormControlLabel
                      value="home"
                      control={<Radio />}
                      label="Home"
                    />
                  </RadioGroup>
                  {formErrors?.serviceLocationType && (
                    <FormHelperText>
                      {formErrors.serviceLocationType}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>

              {patient?.serviceLocationType === "facility" && (
                <>
                  <Grid xs={12} item>
                    <FacilitySelect
                      patient={patient}
                      handleChange={handleChange}
                      required={true}
                      error={formErrors}
                    />
                  </Grid>
                  <Grid xs={12} item>
                    <TextField
                      fullWidth
                      value={metadata?.room}
                      name="room"
                      label="Room #"
                      onChange={handleMetadataChange}
                      sx={inputFieldSyle}
                      InputLabelProps={{
                        shrink: metadata?.room,
                      }}
                    />
                  </Grid>
                </>
              )}

              <Grid xs={12} item>
                <GoogleAddressSelect
                  address={
                    patient?.serviceLocationType === "home"
                      ? patient?.completeAddress
                      : patient?.facilityCompleteAddress
                  }
                  setData={setPatient}
                  errors={formErrors}
                  required={true}
                  label="Service Address"
                  readOnly={patient?.serviceLocationType === "facility"}
                />
              </Grid>

              {patient?.serviceLocationType === "home" && (
                <Grid xs={12} item>
                  <TextField
                    fullWidth
                    value={metadata?.apartmentNumber}
                    name="apartmentNumber"
                    label="Apartment #"
                    onChange={handleMetadataChange}
                    sx={inputFieldSyle}
                    InputLabelProps={{
                      shrink: metadata?.apartmentNumber,
                    }}
                  />
                </Grid>
              )}

              <Grid xs={12} item>
                <h6 className="add-edit-patient-sub-header">
                  Demographic Info
                </h6>
              </Grid>

              <Grid xs={6} item>
                <DatePickerMui
                  setData={setPatient}
                  data={patient}
                  name="birthDate"
                  label="Date Of Birth"
                  inputFieldSyle={inputFieldSyle}
                />
              </Grid>

              <Grid xs={6} item>
                <FormControl sx={{ width: "100%" }} className="add-edit-select">
                  <InputLabel>{strings.GENDER}</InputLabel>
                  <Select
                    name="gender"
                    onChange={handleChange}
                    fullWidth
                    value={patient?.gender || ""}
                    input={<CustomOutlinedInput label={strings.GENDER} />}
                  >
                    {options?.genders?.menuItems?.map((item) => (
                      <MenuItem key={item} value={item}>
                        {item}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid xs={6} item>
                <AddEditPatientSelectField
                  data={options?.maritalStatus ? options?.maritalStatus : {}}
                  metadata={metadata}
                  handleMetadataChange={handleMetadataChange}
                />
              </Grid>
              <Grid xs={6} item>
                <AddEditPatientSelectField
                  data={options?.religions ? options?.religions : {}}
                  metadata={metadata}
                  handleMetadataChange={handleMetadataChange}
                />
              </Grid>

              <Grid xs={12} item>
                <h6 className="add-edit-patient-sub-header">
                  Primary Caregiver / Family Data
                </h6>
              </Grid>

              <Grid xs={6} item>
                <TextField
                  fullWidth
                  value={careGiver?.givenName}
                  name="givenName"
                  label="First Name"
                  variant="outlined"
                  onChange={handleCareGiverChange}
                  sx={inputFieldSyle}
                />
              </Grid>
              <Grid xs={6} item>
                <TextField
                  fullWidth
                  value={careGiver?.familyName}
                  name="familyName"
                  label="Last Name"
                  variant="outlined"
                  onChange={handleCareGiverChange}
                  sx={inputFieldSyle}
                />
              </Grid>

              <Grid xs={12} sm={6} item>
                <MuiTelInput
                  fullWidth
                  defaultCountry="US"
                  value={careGiver?.telephone || ""}
                  forceCallingCode
                  onChange={(value) =>
                    setCareGiver((prev) => ({
                      ...prev,
                      telephone: value,
                    }))
                  }
                  sx={inputFieldSyle}
                />
              </Grid>

              <Grid xs={12} sm={6} item style={{ marginTop: "-8px" }}>
                <FormControl fullWidth className="add-edit-select">
                  <InputLabel>{"Relation with patient"}</InputLabel>
                  <Select
                    name="caregiverRelation"
                    onChange={handleCareGiverChange}
                    value={careGiver?.caregiverRelation || ""}
                    input={
                      <CustomOutlinedInput label={"Relation with patient"} />
                    }
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                          width: 250,
                        },
                      },
                    }}
                  >
                    {options?.caregiverRelation?.menuItems?.map((item) => (
                      <MenuItem key={item} value={item}>
                        {item}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid xs={12} item>
                <GoogleAddressSelect
                  address={careGiver?.completeAddress}
                  setData={setCareGiver}
                />
              </Grid>

              <Grid xs={12} item>
                <h6 className="add-edit-patient-sub-header">
                  Medical and Social History
                </h6>
              </Grid>

              <Grid item xs={12}>
                <DiagnosisSearchAndSelect
                  label="Primary Diagnosis"
                  diagnosis={diagnosis}
                  setDiagnosis={setDiagnosis}
                />
              </Grid>
              <AddEditPatientTextArea
                label="Mental & Physical Status"
                handleMetadataChange={handleMetadataChange}
                metadata={metadata}
                inputFieldSyle={inputFieldSyle}
              />
              <AddEditPatientTextArea
                label="Family/Social History"
                handleMetadataChange={handleMetadataChange}
                metadata={metadata}
                inputFieldSyle={inputFieldSyle}
              />

              <AddEditPatientCheckboxField
                data={options?.codeStatus ? options?.codeStatus : {}}
                setMetadata={setMetadata}
                metadata={metadata}
              />
              <AddEditPatientTextArea
                label="Patient Hobbies/Interest"
                handleMetadataChange={handleMetadataChange}
                metadata={metadata}
                inputFieldSyle={inputFieldSyle}
              />

              <Grid xs={12} item>
                <TextField
                  fullWidth
                  value={patient?.comments}
                  multiline
                  rows={2}
                  name="comments"
                  label="Notes"
                  onChange={handleChange}
                  sx={{
                    height: "auto",
                    "& .MuiInputBase-root": { height: "auto" },
                  }}
                  InputLabelProps={{
                    shrink: patient?.comments,
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </>
      )}
    </div>
  );
};

export default AddEditPatientFormComponent;
